import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { PageService, PageInterface } from '@app/services/page.service';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from '@app/services/session.service';
import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import {
	FormationThematique,
	FormationThematiqueSearchParams,
} from '@app/models/formation-thematique';
import { FormationThematiqueService } from '@app/models/formation-thematique/formation-thematique.service';
import { User } from '@app/models/user';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
	selector: 'app-molecule-formation-thematique-list',
	templateUrl: './molecule-formation-thematique-list.component.html',
	styleUrls: ['./molecule-formation-thematique-list.component.less'],
})
export class MoleculeFormationThematiqueListComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	protected skeleton: PageSkeletonType = 'list';

	user: User;
	thematiques: FormationThematique[] = [];
	searchParams: FormationThematiqueSearchParams;
	sortOptions = [
		{
			sort: 'start_date',
			order: 'desc',
			label: 'thematique_list-sort-start_date_desc',
		},
		{
			sort: 'start_date',
			order: 'asc',
			label: 'thematique_list-sort-start_date_asc',
		},
		{ sort: 'name', order: 'asc', label: 'thematique_list-sort-name_asc' },
		{
			sort: 'name',
			order: 'desc',
			label: 'thematique_list-sort-name_desc',
		},
	];
	selectedSort = 'start_date_desc';
	mode: 'inline' | 'card' = 'card';
	loading = false;
	limit = 100;
	currentPageIndex = 1;
	totalItems = 0;
	paginationSubject = new BehaviorSubject<number>(0);
	private unsubscribe$ = new Subject<void>();

	constructor(
		private sessionService: SessionService,
		protected pageService: PageService,
		protected translate: TranslateService,
		private formationThematiqueService: FormationThematiqueService
	) {
		super(pageService, translate);
	}

	async ngOnInit() {
		super.ngOnInit();
		this.user = await this.sessionService.getCachedUser();
		this.searchParams = new FormationThematiqueSearchParams({
			_page: 0,
			_limit: this.limit,
			_order: 'desc',
			_sort: 'start_date',
			_populate: ['color', 'formation.sponsor.avatar'],
			formateur: this.user.getId(),
		});

		this.paginationSubject
			.asObservable()
			.pipe(
				takeUntil(this.unsubscribe$),
				tap((newIndex) => {
					const realIndex = newIndex > 1 ? newIndex - 1 : 0;
					this.searchParams.props._page = realIndex;
					this.searchParams.next();
				})
			)
			.subscribe();

		this.searchParams
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(async () => {
				await this.getThematiqueList();
			});
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	trackById(_: number, thematique: FormationThematique): string {
		return thematique.getId();
	}

	async getThematiqueList() {
		this.loading = true;
		const res = await this.formationThematiqueService.list(
			this.searchParams.toObject()
		);
		this.thematiques = res.items;
		this.totalItems = res.total;
		this.loading = false;
	}

	handleNextPage(index: number) {
		this.currentPageIndex = index;
		this.paginationSubject.next(index);
	}

	handleSortChange() {
		const option = this.sortOptions.find(
			(o) => o.sort + '_' + o.order === this.selectedSort
		);
		if (option) {
			this.searchParams.props._sort = option.sort as any;
			this.searchParams.props._order = option.order as any;
		}
		this.currentPageIndex = 1;
		this.searchParams.props._page = 0;
		this.searchParams.next();
	}

	handleFormationFilterChange() {
		this.currentPageIndex = 1;
		this.searchParams.props._page = 0;
		this.searchParams.next();
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant(
				'page_title-formation_thematique_list'
			),
			breadcrumbs: [
				{
					label: this.translate.instant(
						'top_bar-formation_thematique_list'
					),
				},
			],
			hideTopBar: false,
			hideMenu: false,
		};
	}
}
